import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Banknote, CreditCard, Loader2 } from "lucide-react";
import { api, uid } from "../lib/api";
import { ERRORS } from "../lib/i18n";
import { useLang } from "../context/LangContext";
import { useCart } from "../context/CartContext";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import { OrderSummary } from "../components/store/OrderSummary";
import { CheckoutForm, validateForm } from "../components/store/CheckoutForm";
import { ReviewBlock } from "../components/store/ReviewBlock";

const EMPTY = { name: "", phone: "", email: "", city: "", district: "", street: "", building_no: "", postal_code: "", additional_no: "", short_address: "", notes: "" };

export default function Checkout() {
  const { t, lang } = useLang();
  const { items, clear } = useCart();
  const { settings } = useStore();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(JSON.parse(sessionStorage.getItem("qamar_checkout") || "{}")) }));
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);
  const [quote, setQuote] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [placing, setPlacing] = useState(false);
  const [payCfg, setPayCfg] = useState({ enabled: false });
  const codOn = settings?.payment?.cod_enabled !== false;
  const [method, setMethod] = useState("cod");
  const idem = useRef(uid());

  useEffect(() => {
    api.get("/payments/config").then((r) => {
      setPayCfg(r.data);
      if (r.data.enabled && !codOn) setMethod("online");
    });
  }, [codOn]);

  useEffect(() => {
    if (user && !form.name) setForm((f) => ({ ...f, name: user.name || "", email: user.email || "" }));
  }, [user]); // eslint-disable-line

  useEffect(() => {
    sessionStorage.setItem("qamar_checkout", JSON.stringify(form));
  }, [form]);

  const payload = () => items.map(({ product_id, size, color, qty }) => ({ product_id, size, color, qty }));

  useEffect(() => {
    if (!items.length) return;
    api.post("/cart/quote", { items: payload(), city: form.city, coupon_code: coupon || null }).then((r) => {
      setQuote(r.data);
      if (coupon && r.data.coupon_error) {
        toast.error(ERRORS[r.data.coupon_error]?.[lang] || t("coupon"));
        setCoupon("");
      }
    });
  }, [items, form.city, coupon]); // eslint-disable-line

  if (!items.length && !placing) {
    return (
      <div className="container-q py-32 text-center">
        <p className="font-display text-3xl" data-testid="checkout-empty">{t("empty_cart")}</p>
        <button className="btn-line mt-8" onClick={() => nav("/shop/all")}>{t("continue_shopping")}</button>
      </div>
    );
  }

  const goReview = () => {
    const errs = validateForm(form);
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error(ERRORS[Object.values(errs)[0]]?.[lang]);
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const place = async () => {
    if (placing) return;
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        items: payload(), coupon_code: coupon || null, payment_method: method, idempotency_key: idem.current,
        customer: { name: form.name, phone: form.phone, email: form.email || null },
        address: { city: form.city, district: form.district, street: form.street, building_no: form.building_no, postal_code: form.postal_code, additional_no: form.additional_no, short_address: form.short_address, notes: form.notes },
      });
      clear();
      sessionStorage.removeItem("qamar_checkout");
      nav(method === "online" ? `/pay/${data.order_number}?token=${data.access_token}` : `/order/${data.order_number}?token=${data.access_token}`, { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = typeof d === "string" ? d : d?.code;
      toast.error(ERRORS[code]?.[lang] || t("not_found"));
      setPlacing(false);
      if (code === "cart_invalid") setStep(1);
    }
  };

  const couponBox = (
    <div className="mt-6 flex items-end gap-3">
      <input data-testid="coupon-input" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder={t("coupon")} className="field flex-1" />
      <button data-testid="coupon-apply-button" onClick={() => setCoupon(couponInput.trim())} className="btn-line px-5 py-2.5">{t("apply")}</button>
    </div>
  );

  return (
    <div data-testid="checkout-page" className="container-q pb-28 pt-12 sm:pt-16">
      <div className="mb-10 flex items-center gap-6 text-sm">
        {[t("step_info"), t("step_review")].map((s, i) => (
          <span key={s} className={`flex items-center gap-3 ${step === i + 1 ? "text-ink" : "text-sand-dark"}`}>
            <span className={`grid h-7 w-7 place-items-center rounded-full border text-xs ${step === i + 1 ? "border-ink bg-ink text-ivory" : "border-sand"}`}>{i + 1}</span>{s}
          </span>
        ))}
      </div>
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          {step === 1 ? (
            <>
              <CheckoutForm form={form} setForm={setForm} errors={errors} cities={settings?.cities || []} />
              <button data-testid="checkout-review-button" onClick={goReview} className="btn-ink mt-10 w-full sm:w-auto">{t("review_order")}</button>
            </>
          ) : (
            <>
              <ReviewBlock form={form} onEdit={() => setStep(1)} />
              <div className="mt-8 space-y-3">
                <p className="text-xs tracking-[0.1em] text-sand-dark">{t("payment_method")}</p>
                {[["online", CreditCard, t("online_pay"), t("online_desc"), payCfg.enabled], ["cod", Banknote, t("cod"), t("cod_desc"), codOn]].filter((x) => x[4]).map(([k, Icon, l, dsc]) => (
                  <button key={k} type="button" data-testid={`payment-${k}`} onClick={() => setMethod(k)}
                    className={`flex w-full items-start gap-3 border p-5 text-start transition-colors ${method === k ? "border-ink bg-white" : "border-sand"}`}>
                    <span className={`mt-1 h-4 w-4 shrink-0 rounded-full border ${method === k ? "border-[5px] border-ink" : "border-sand-dark"}`} />
                    <span><span className="flex items-center gap-2"><Icon size={18} strokeWidth={1.3} className="text-gold-deep" />{l}</span><span className="mt-1 block text-xs text-ink/60">{dsc}</span></span>
                  </button>
                ))}
              </div>
              <button data-testid="place-order-button" disabled={placing || !quote || quote.errors?.length > 0} onClick={place} className="btn-ink mt-8 w-full">
                {placing ? <><Loader2 size={16} className="animate-spin" />{t("placing")}</> : method === "online" ? t("continue_pay") : t("place_order")}
              </button>
            </>
          )}
        </div>
        <div className="lg:col-span-5">
          <OrderSummary quote={quote} items={items}>{couponBox}</OrderSummary>
        </div>
      </div>
    </div>
  );
}
